import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { AppleVerifyResponse } from "./type";

interface AppleVerifyDetailsProps {
  data: AppleVerifyResponse | null;
  isLoading?: boolean;
}

const AppleVerifyDetails: React.FC<AppleVerifyDetailsProps> = ({
  data,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card elevation={3}>
        <CardContent>
          <Typography>Veriler yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card elevation={3}>
        <CardContent>
          <Typography color="error">
            Apple doğrulama bilgileri bulunamadı.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Format MS date to readable format
  const formatMsDate = (dateMs?: string) => {
    if (!dateMs) return "-";
    try {
      return new Date(parseInt(dateMs)).toLocaleString();
    } catch (error) {
      console.error("Date parsing error:", error);
      return dateMs;
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Apple Doğrulama Bilgileri
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
          <Grid container spacing={2}>
            {/* Status and Environment */}
            <Grid size={{ xs: 4 }}>
              <Typography variant="subtitle2">Durum:</Typography>
            </Grid>
            <Grid size={{ xs: 8 }}>
              <Chip
                label={data.status === 0 ? "Başarılı" : `Hata: ${data.status}`}
                color={data.status === 0 ? "success" : "error"}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 4 }}>
              <Typography variant="subtitle2">Ortam:</Typography>
            </Grid>
            <Grid size={{ xs: 8 }}>
              <Typography>{data.environment || "-"}</Typography>
            </Grid>

            {/* Receipt Information */}
            {data.receipt && (
              <>
                <Grid size={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Makbuz Bilgileri
                  </Typography>
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <Typography variant="subtitle2">Bundle ID:</Typography>
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <Typography>{data.receipt.bundle_id || "-"}</Typography>
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <Typography variant="subtitle2">
                    Uygulama Versiyonu:
                  </Typography>
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <Typography>
                    {data.receipt.application_version || "-"}
                  </Typography>
                </Grid>
              </>
            )}

            {/* In-App Purchase Details */}
            {data.receipt?.in_app && data.receipt.in_app.length > 0 && (
              <>
                <Grid size={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Uygulama İçi Satın Alma Detayları
                  </Typography>
                </Grid>

                {data.receipt.in_app.map((purchase, index) => (
                  <React.Fragment key={purchase.transaction_id || index}>
                    {index > 0 && (
                      <Grid size={12}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                    )}

                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle2">Miktar:</Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography>{purchase.quantity || "-"}</Typography>
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle2">Ürün ID:</Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography>{purchase.product_id || "-"}</Typography>
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle2">İşlem ID:</Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography sx={{ wordBreak: "break-all" }}>
                        {purchase.transaction_id || "-"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle2">
                        Orijinal İşlem ID:
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography sx={{ wordBreak: "break-all" }}>
                        {purchase.original_transaction_id || "-"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle2">
                        Satın Alma Tarihi:
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography>
                        {formatMsDate(purchase.purchase_date_ms)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 4 }}>
                      <Typography variant="subtitle2">
                        Orijinal Satın Alma Tarihi:
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Typography>
                        {formatMsDate(purchase.original_purchase_date_ms)}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </>
            )}

            {/* Latest Receipt Info */}
            {data.latest_receipt_info &&
              data.latest_receipt_info.length > 0 && (
                <>
                  <Grid size={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Son Makbuz Bilgileri
                    </Typography>
                  </Grid>

                  {data.latest_receipt_info.map((receipt, index) => (
                    <React.Fragment key={receipt.transaction_id || index}>
                      {index > 0 && (
                        <Grid size={12}>
                          <Divider sx={{ my: 2 }} />
                        </Grid>
                      )}

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="subtitle2">Ürün ID:</Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography>{receipt.product_id || "-"}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="subtitle2">İşlem ID:</Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography sx={{ wordBreak: "break-all" }}>
                          {receipt.transaction_id || "-"}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="subtitle2">
                          Orijinal İşlem ID:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography sx={{ wordBreak: "break-all" }}>
                          {receipt.original_transaction_id || "-"}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="subtitle2">
                          Satın Alma Tarihi:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography>
                          {formatMsDate(receipt.purchase_date_ms)}
                        </Typography>
                      </Grid>
                    </React.Fragment>
                  ))}
                </>
              )}
          </Grid>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default AppleVerifyDetails;
